import { Loader, Warning } from 'components/Base';
import React, { useRef } from 'react';

import { BackButton, Button } from 'components/Buttons';
import Form from 'components/Form';
import { env } from 'env';
import { useNavigate } from 'react-router-dom';
import * as dateUtils from 'utils/dateUtils';

import classNames from 'classnames';
import ReactToPrint from 'react-to-print';
import NotFound from 'pages/NotFound';
import { formatPercents, formatUSDollarsWithCents } from 'utils/numberFormatters';

import { SupportedState, useTilaBoxData } from './useTilaBoxData';

import styles from './TilaBoxPage.module.scss';

const EXPIRE_IN_DAYS = 20;

const TilaBoxPage = () => {
  const {
    application,
    applicationLoading,
    borrowerLoading,
    defaultTilaBoxValues,
    tilaBoxValuesByState,
  } = useTilaBoxData();

  const navigate = useNavigate();
  const printableRef = useRef<HTMLDivElement>(null);

  if (applicationLoading || borrowerLoading) {
    return <Loader />;
  }

  const renderFormFooter = () => (
    <div className="buttons">
      <BackButton onClick={() => navigate(`${env.REACT_APP_BASE_URL}/`)}>Back</BackButton>
    </div>
  );

  const renderDisclosure = () => {
    if (!application?.createdAt) {
      return null;
    }

    const expirationDate = dateUtils.getExpirationDate(new Date(application.createdAt), EXPIRE_IN_DAYS);

    return (
      <>
        <p className={classNames('caption', styles.text, styles.disclosure)}>
          For more information regarding credit terms and your Truth-in-Lending disclosure, please click “Sign/Review
          Documents.” on the prior page. Upon clicking, you will have the ability to review, download, and save a copy
          of your loan documents in the online platform before signing.
        </p>
        <p className={classNames('caption', styles.text)}>
          * All Republic Finance loans are subject to a binding Arbitration Agreement. This offer will expire in 20 days
          on {expirationDate.getMonth() + 1}/{expirationDate.getDate()}/{expirationDate.getFullYear()}.
        </p>
      </>
    );
  };

  if (!defaultTilaBoxValues || !tilaBoxValuesByState) {
    return <NotFound />;
  }

  const {
    amountFinanced,
    apr,
    currentDatePlus32,
    financeCharge,
    firstPaymentAmount,
    loanBalance,
    numberOfPaymentsMinusOne,
    regularPaymentAmount,
  } = defaultTilaBoxValues;

  const {
    amountPaidToUs,
    amountPaidToYou,
    prepaidFinanceCharges,
    docStampTax,
    principalBalance,
  } = tilaBoxValuesByState;

  const renderItemizationByState = (state: SupportedState) => {
    switch (state) {
      case SupportedState.TX:
        return (
          <>
            <li className="text_small">
              <p>1. Amount Paid to you (Customer)</p>
              <p>{formatUSDollarsWithCents(amountPaidToYou!)}</p>
            </li>
            <li className="text_small">
              <p>2. Amount Paid to Republic Finance for Prepaid Finance Charge</p>
              <p>{formatUSDollarsWithCents(amountPaidToUs!)}</p>
            </li>
            <li className="text_small">
              <p>3. Total Principal Balance (1+2)</p>
              <p>{formatUSDollarsWithCents(principalBalance!)}</p>
            </li>
            <li className="text_small">
              <p>4. (Less) Prepaid Finance Charges </p>
              <p>{formatUSDollarsWithCents(prepaidFinanceCharges!)}</p>
            </li>
            <li className="text_small">
              <p>5. Amount Financed (3 minus 4)</p>
              <p>{formatUSDollarsWithCents(amountFinanced)}</p>
            </li>
          </>
        );
      case SupportedState.FL:
        return (
          <>
            <li className="text_small">
              <p>1. Amount Paid to you (Customer)</p>
              <p>{formatUSDollarsWithCents(amountPaidToYou!)}</p>
            </li>
            <li className="text_small">
              <p>2. Amount Paid to Republic Finance for Prepaid Finance Charge</p>
              <p>{formatUSDollarsWithCents(amountPaidToUs!)}</p>
            </li>
            <li className="text_small">
              <p>3. Amount Paid to Public Official (Documentary Stamp Taxes)</p>
              <p>{formatUSDollarsWithCents(docStampTax!)}</p>
            </li>
            <li className="text_small">
              <p>4. (Less) Prepaid Finance Charges</p>
              <p>{formatUSDollarsWithCents(prepaidFinanceCharges!)}</p>
            </li>
            <li className="text_small">
              <p>5. Amount Financed (Sum of 1, 2 & 3, less 4)</p>
              <p>{formatUSDollarsWithCents(amountFinanced)}</p>
            </li>
          </>
        );
      default:
        return (
          <>
            <li className="text_small">
              <p>1. Amount Paid to you (Customer)</p>
              <p>{formatUSDollarsWithCents(amountPaidToYou!)}</p>
            </li>
            <li className="text_small">
              <p>2. Amount Paid to Republic Finance for Prepaid Finance Charge</p>
              <p>{formatUSDollarsWithCents(amountPaidToUs!)}</p>
            </li>
            <li className="text_small">
              <p>3. (Less) Prepaid Finance Charges</p>
              <p>{formatUSDollarsWithCents(prepaidFinanceCharges!)}</p>
            </li>
            <li className="text_small">
              <p>4. Amount Financed (sum of 1 & 2, less 3)</p>
              <p>{formatUSDollarsWithCents(amountFinanced)}</p>
            </li>
          </>
        );
    }
  };

  return (
    <Form title="Truth-In-Lending Disclosure Statement" renderFooter={renderFormFooter}>
      <Warning className={styles.credit_score_disclosure_warning}>
        You may want to print or save this page for your records.
      </Warning>
      <div ref={printableRef}>
        <div className={styles.address_section}>
          <p className="subtitle_small">Republic Finance, LLC</p>
          <p className="caption">7031 Commerce Circle</p>
          <p className="caption">Baton Rouge LA 70809</p>
        </div>
        <div className={styles.wrapper}>
          <div className={styles.title_wrapper}>
            <p className={classNames('subtitle_large', styles.dark_blue)}>Unsecured Loan</p>
            <p className={classNames('caption', styles.dark_blue)}>Only Available Online</p>
          </div>
          <div className={styles.loan_terms_title_wrapper}>
            <p className="subtitle_small">Loan Terms:</p>
          </div>
          <div className={styles.loan_term_wrapper}>
            <div>
              <p className="subtitle_small">ANNUAL PERCENTAGE RATE</p>
              <p className="text_small">The cost of your loan as a yearly rate</p>
              <p className="text_large">{formatPercents(apr)}</p>
            </div>
            <div>
              <p className="subtitle_small">FINANCE CHARGE</p>
              <p className="text_small">The dollar amount your loan will cost you</p>
              <p className="text_large">{formatUSDollarsWithCents(financeCharge)}</p>
            </div>
            <div>
              <p className="subtitle_small">Amount Financed</p>
              <p className="text_small">The amount of credit provided to you or on your behalf</p>
              <p className="text_large">{formatUSDollarsWithCents(amountFinanced)}</p>
            </div>
            <div>
              <p className="subtitle_small">Total of Payments</p>
              <p className="text_small">The amount you will have paid after you have made all payments as scheduled</p>
              <p className="text_large">{formatUSDollarsWithCents(loanBalance)}</p>
            </div>
          </div>
          <div className={styles.payment_schedule_title_wrapper}>
            <p className="subtitle_small">Payment Schedule:</p> <p>Your loan is repayable as follows:</p>
          </div>
          <div className={styles.payment_schedule_wrapper}>
            <div>
              <p className="subtitle_small">Number of Payments</p>
            </div>
            <div>
              <p className="subtitle_small">Amount</p>
            </div>
            <div>
              <p className="subtitle_small">When Payments Are Due</p>
            </div>
            <div>
              <p className="text_small">An Initial Installment Payment of:</p>
            </div>
            <div>
              <p className="text_small">{formatUSDollarsWithCents(firstPaymentAmount)}</p>
            </div>
            <div>
              <p className="text_small">Due on {currentDatePlus32}. follow by</p>
            </div>
            <div>
              <p className="text_small">{numberOfPaymentsMinusOne} Equal Installment Payments of:</p>
            </div>
            <div>
              <p className="text_small">{formatUSDollarsWithCents(regularPaymentAmount)}</p>
            </div>
            <div>
              <p className="text_small">On the same day of each month thereafter </p>
            </div>
          </div>
          <div className={styles.terms_wrapper}>
            <p className="text_small">
              <span className="subtitle_small">Late Payment:</span> If a scheduled payment is 15 days or more late, You
              agree to pay a late charge equal to 5% of the portion of the scheduled payment in default or $15.00
              whichever is greater, not to exceed $50.00
            </p>
            <p className="text_small">
              <span className="subtitle_small">Prepayment:</span> If you prepay in full or part, you will not have to
              pay a penalty. You may be entitled to a refund of part of the finance charge.
            </p>
            <p className="text_small">
              <span className="subtitle_small">Contract Reference:</span> You should look to your Note for additional
              information about non-payment, default, any required repayment in full before the scheduled date, and
              prepayment refunds.
            </p>
          </div>
          <div className={styles.itemization_title_wrapper}>
            <p className="subtitle_small">Itemization of the Amount Financed</p>
          </div>
          <ol className={styles.itemization_wrapper}>
            {renderItemizationByState(application!.variables.borrower_state_or_province as SupportedState)}
          </ol>
        </div>
      </div>
      <div className={styles.print_button}>
        <ReactToPrint trigger={() => <Button>Print</Button>} content={() => printableRef.current} />
      </div>
      {renderDisclosure()}
    </Form>
  );
};

export default TilaBoxPage;
